/**
 * Design tokens for RecoveryAssure.
 *
 * Severity + feasibility colours match the Phase 5 brief. Never hard-code
 * colours elsewhere — always import from here so accessibility labels stay
 * consistent with the visual palette.
 */

export const SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "REVIEW"];

export const SEVERITY_META = {
  CRITICAL: {
    label: "Critical",
    bg: "bg-red-100",
    text: "text-red-800",
    ring: "ring-red-300",
    aria: "critical severity",
    dot: "bg-red-500",
  },
  HIGH: {
    label: "High",
    bg: "bg-orange-100",
    text: "text-orange-800",
    ring: "ring-orange-300",
    aria: "high severity",
    dot: "bg-orange-500",
  },
  MEDIUM: {
    label: "Medium",
    bg: "bg-amber-100",
    text: "text-amber-900",
    ring: "ring-amber-300",
    aria: "medium severity",
    dot: "bg-amber-500",
  },
  LOW: {
    label: "Low",
    bg: "bg-slate-100",
    text: "text-slate-700",
    ring: "ring-slate-300",
    aria: "low severity",
    dot: "bg-slate-500",
  },
  REVIEW: {
    label: "Review",
    bg: "bg-sky-100",
    text: "text-sky-800",
    ring: "ring-sky-300",
    aria: "review flag",
    dot: "bg-sky-500",
  },
};

export const FEASIBILITY_META = {
  FEASIBLE: {
    label: "Feasible",
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
    aria: "recovery feasible",
    dot: "bg-emerald-500",
  },
  AT_RISK: {
    label: "At Risk",
    bg: "bg-amber-100",
    text: "text-amber-900",
    border: "border-amber-200",
    aria: "recovery at risk",
    dot: "bg-amber-500",
  },
  NOT_FEASIBLE: {
    label: "Not Feasible",
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-200",
    aria: "recovery not feasible",
    dot: "bg-red-500",
  },
  INSUFFICIENT_DATA: {
    label: "Insufficient Data",
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200 border-dashed",
    aria: "insufficient data",
    dot: "bg-slate-400",
    pattern: true, // dashed border distinguishes "unknown" from "fine"
  },
};

export const CONFIDENCE_META = {
  HIGH: { label: "High", bg: "bg-emerald-50", text: "text-emerald-800", bar: 100 },
  MEDIUM: { label: "Medium", bg: "bg-amber-50", text: "text-amber-800", bar: 66 },
  LOW: { label: "Low", bg: "bg-orange-50", text: "text-orange-800", bar: 33 },
  CANNOT_DETERMINE: {
    label: "Cannot Determine",
    bg: "bg-slate-50",
    text: "text-slate-700",
    bar: 0,
  },
};

export const DIMENSION_ORDER = [
  "TIME", "TECHNOLOGY", "DATA", "CAPACITY", "PEOPLE",
  "SUPPLIERS", "LOCATION", "DEPENDENCIES", "VALIDATION",
];

export const DIMENSION_LABELS = {
  TIME: "Time",
  TECHNOLOGY: "Technology",
  DATA: "Data",
  CAPACITY: "Capacity",
  PEOPLE: "People",
  SUPPLIERS: "Suppliers",
  LOCATION: "Location",
  DEPENDENCIES: "Dependencies",
  VALIDATION: "Validation Evidence",
};

export const DIMENSION_STATUS_META = {
  PASS: { label: "PASS", text: "text-emerald-700", dot: "bg-emerald-500" },
  GAP: { label: "GAP", text: "text-red-700", dot: "bg-red-500" },
  UNKNOWN: { label: "UNKNOWN", text: "text-slate-600", dot: "bg-slate-400" },
  NOT_APPLICABLE: { label: "N/A", text: "text-slate-500", dot: "bg-slate-300" },
};

// Fact source labels for the FactChip component. INFERRED / RECOMMENDED are
// scaffolded but not emitted by the Phase 5 UI — they're reserved for Phase 6.
export const FACT_SOURCE_META = {
  SOURCE_FACT: {
    label: "SOURCE FACT",
    text: "text-slate-700",
    bg: "bg-slate-100",
    aria: "source fact from imported data",
  },
  CALCULATED: {
    label: "CALCULATED",
    text: "text-sky-800",
    bg: "bg-sky-100",
    aria: "calculated by the deterministic engine",
  },
  FINDING: {
    label: "FINDING",
    text: "text-red-800",
    bg: "bg-red-100",
    aria: "engine finding",
  },
  INFERRED: {
    label: "INFERRED",
    text: "text-violet-800",
    bg: "bg-violet-100",
    aria: "inferred by the AI reviewer (Phase 6)",
  },
  RECOMMENDED: {
    label: "RECOMMENDED",
    text: "text-indigo-800",
    bg: "bg-indigo-100",
    aria: "recommendation from the AI reviewer (Phase 6)",
  },
};
